import React from 'react';
import { Loader2, RefreshCw, SearchX, WifiOff, ServerCrash, Timer } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { BrowseStatus } from '@/lib/marketplace';

/**
 * The five marketplace feed states, kept deliberately distinct.
 *
 * LAUNCH-BLOCKER CONTEXT: the marketplace used to render the "no results" card
 * for every one of these situations, which told mobile users the marketplace was
 * empty while 51 listings were published. A failure is now always labelled as a
 * failure and always offers a Retry.
 */
export type FeedState = 'loading' | 'empty' | BrowseStatus;

const BrowseState: React.FC<{
  state: Exclude<FeedState, 'ok'>;
  retryAfter?: number;
  onRetry: () => void;
  onClearFilters?: () => void;
  hasFilters?: boolean;
}> = ({ state, retryAfter = 0, onRetry, onClearFilters, hasFilters }) => {
  const { t } = useI18n();

  if (state === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-12 text-center"
      >
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <p className="mt-3 text-sm font-semibold text-slate-500">{t('jobs.loading')}</p>
      </div>
    );
  }

  const minutes = Math.max(1, Math.ceil((retryAfter || 60) / 60));

  const config: Record<
    string,
    { icon: React.ReactNode; message: string; tone: string; retry: boolean }
  > = {
    empty: {
      icon: <SearchX className="h-6 w-6 text-slate-400" />,
      message: t('jobs.empty'),
      tone: 'border-dashed border-slate-300 bg-white text-slate-500',
      retry: false,
    },
    rate_limited: {
      icon: <Timer className="h-6 w-6 text-amber-500" />,
      message: t('jobs.errRateWait').replace('{minutes}', String(minutes)),
      tone: 'border-amber-200 bg-amber-50 text-amber-800',
      retry: true,
    },
    network_error: {
      icon: <WifiOff className="h-6 w-6 text-slate-500" />,
      message: t('jobs.errNetwork'),
      tone: 'border-slate-300 bg-slate-50 text-slate-700',
      retry: true,
    },
    server_error: {
      icon: <ServerCrash className="h-6 w-6 text-red-500" />,
      message: t('jobs.errLoad'),
      tone: 'border-red-200 bg-red-50 text-red-800',
      retry: true,
    },
  };

  const c = config[state] || config.server_error;

  return (
    <div
      role={state === 'empty' ? 'status' : 'alert'}
      aria-live="polite"
      className={`flex flex-col items-center justify-center rounded-2xl border p-10 text-center sm:p-12 ${c.tone}`}
    >
      {c.icon}
      <p className="mt-3 max-w-md text-sm font-semibold">{c.message}</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        {c.retry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4" /> {t('jobs.retry')}
          </button>
        )}
        {state === 'empty' && hasFilters && onClearFilters && (
          <button
            onClick={onClearFilters}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            {t('jobs.clearFilters')}
          </button>
        )}
      </div>
    </div>
  );
};

export default BrowseState;
